// Секция «Список наблюдения»: un grafic mare + lista de urmărire în dreapta.

import { watchUrl } from '../ui/alert-banners.js';
import { el, clear, bus, debounce, fmtVolume, fmtPct } from '../core/util.js';
import { icon } from '../ui/icons.js';
import { settings, market } from '../core/state.js';
import { ChartPanel } from '../ui/chart-panel.js';
import { coinRow, baseTicker, toast, modal, toggle, dropdown } from '../ui/components.js';
import { iconMoneda, iconBursa } from '../ui/coin-icon.js';
import { SORT_TYPES, comparator } from '../core/filters.js';
import { attachSearchDropdown } from '../ui/search-dropdown.js';

/** Sortările ciclabile din antetul listei. */
const SORTS = [
  { id: 'natr5', label: 'NATR 5м/14', field: 'natr5' },
  { id: 'range5', label: 'Ренж 1м/5', field: 'range5' },
  { id: 'volume', label: 'Объём 24ч', field: 'quoteVolume' },
  { id: 'top_growth', label: 'Изменение 24ч', field: 'change24h' },
  { id: 'volatility', label: 'Волатильность', field: 'volatility24h' },
  { id: 'trades', label: 'Сделки', field: 'trades' },
  { id: 'volume_spike', label: 'Всплеск объёма', field: 'volTrend' },
  { id: 'natr', label: 'NATR', field: 'natr' },
  { id: 'funding', label: 'Фандинг', field: 'funding' },
];

const MAX_ROWS = 800;   // cu ambele burse sunt ~2× mai multe monede

/**
 * Filtrele combinate — NATR, ренж, oborot, volatilitate — ca la scalperii care caută monede
 * „în mișcare": NATR 5м/14 spune cât se mișcă moneda de obicei pe o lumânare de 5 minute,
 * ренж 1м/5 — cât a mers în ultimele 5 minute, adică dacă se mișcă **acum**.
 */
const FILTERS = [
  { key: 'natr5', prefix: 'NATR 5м', lead: 'fa-wave-square',
    title: 'Средний ход за 5-минутную свечу (ATR 14), % от цены. Считается по фьючерсам',
    opts: [[0, 'любой'], [0.3, '≥0.3%'], [0.5, '≥0.5%'], [0.8, '≥0.8%'], [1, '≥1%'], [1.5, '≥1.5%'], [2, '≥2%']] },
  { key: 'range5', prefix: 'Ренж 1м/5', lead: 'fa-arrows-up-down',
    title: 'Сколько прошла цена за последние 5 минутных свечей (макс − мин), % — движется ли монета прямо сейчас',
    opts: [[0, 'любой'], [0.3, '≥0.3%'], [0.5, '≥0.5%'], [1, '≥1%'], [2, '≥2%'], [3, '≥3%']] },
  { key: 'vol', prefix: 'Объём', lead: 'coins', title: 'Оборот за 24 часа',
    opts: [[0, 'любой'], [5e6, '≥$5M'], [10e6, '≥$10M'], [20e6, '≥$20M'], [50e6, '≥$50M'], [70e6, '≥$70M'], [100e6, '≥$100M']] },
  { key: 'volat', prefix: 'Волат. 24ч', lead: 'fa-chart-area', title: 'Размах дня: (максимум − минимум) / минимум, %',
    opts: [[0, 'любая'], [5, '≥5%'], [10, '≥10%'], [15, '≥15%'], [20, '≥20%']] },
];
const FILTER_PRESETS = [
  // Ca în screenerul scalperului din video: doar monede lichide (≥ $70M pe 24h, ordin de
  // 5–10k$ fără alunecare mare), ordonate după cât se mișcă o lumânare de 5 minute.
  { id: 'effective', label: 'Эффективный сетап', icon: 'fa-crosshairs', f: { natr5: 0, range5: 0, vol: 70e6, volat: 0 }, sort: 'natr5',
    title: 'Оборот ≥ $70M за 24 часа, сортировка по NATR 5м/14 — ликвидные монеты, которые сильнее всего ходят' },
  { id: 'scalp', label: 'Скальпинг', icon: 'fa-bolt', f: { natr5: 1, range5: 0, vol: 20e6, volat: 0 }, sort: 'natr5',
    title: 'NATR 5м ≥1% и оборот ≥ $20M — монеты, на которых ход за свечу покрывает комиссию с запасом' },
  { id: 'now', label: 'Движение сейчас', icon: 'fire', f: { natr5: 0, range5: 1, vol: 10e6, volat: 0 }, sort: 'range5',
    title: 'Прошли ≥1% за последние 5 минут при обороте ≥ $10M' },
  { id: 'volatile', label: 'Волатильные', icon: 'fa-chart-area', f: { natr5: 1.5, range5: 0, vol: 5e6, volat: 10 }, sort: 'natr5',
    title: 'NATR 5м ≥1.5% и размах дня ≥10%' },
  { id: 'liquid', label: 'Ликвидные', icon: 'fa-droplet', f: { natr5: 0.5, range5: 0, vol: 100e6, volat: 0 }, sort: 'volume',
    title: 'Оборот ≥ $100M и NATR ≥0.5%' },
  { id: 'all', label: 'Все монеты', icon: 'fa-border-all', f: { natr5: 0, range5: 0, vol: 0, volat: 0 }, sort: 'volume',
    title: 'Без фильтров' },
];
const FILTER_KEY = 'sx_coins_filters';

/**
 * Bursa listei: Binance, Bybit sau amândouă. Lista își ia singură datele bursei alese
 * (nu schimbă bursa întregii aplicații), iar graficul se deschide pe bursa monedei.
 */
const EX_KEY = 'sx_coins_ex';
const EX_CHART_KEY = 'sx_coins_chart_ex';
const EXCHANGES = [
  { value: 'binance', label: 'Binance', nod: () => iconBursa('binance', 13) },
  { value: 'bybit', label: 'Bybit', nod: () => iconBursa('bybit', 13) },
  { value: 'both', label: 'Обе биржи', icon: 'fa-layer-group' },
];
const DATA_MS = 5000;
const SPARK_MS = 60000;

/** NATR 5м: exact, din lumânări de 1m; pe spot (unde nu se calculează) — estimat din cel de 15m. */
const natr5Of = (r) => Number(r.natr5) || (Number(r.natr) ? Number(r.natr) / Math.sqrt(3) : 0);

const fmtP = (x, d = 1) => (x === null || x === undefined || Number.isNaN(x) ? '—' : Number(x).toFixed(d));

export class CoinsSection {
  constructor(app) {
    this.app = app;
    this.search = '';
    this.exSel = settings.get(EX_KEY, app.exchange === 'bybit' ? 'bybit' : 'binance');
    this.data = new Map();        // `${bursă}:${piață}` -> {simbol: rând}
    this.sparks = new Map();      // `${bursă}:${piață}` -> Map(simbol -> sparkline)
    this.tab = app.market;                        // futures | spot | bookmarks
    const sortId = settings.get('sx_coins_sort', 'natr5');
    this.sortIndex = Math.max(0, SORTS.findIndex((x) => x.id === sortId));
    this.desc = true;
    this.unsubs = [];
    this.flt = { natr5: 0, range5: 0, vol: 0, volat: 0, preset: 'all', ...(settings.get(FILTER_KEY, {}) || {}) };
    this.view = settings.get('sx_coins_view', 'table');
  }

  mount(host) {
    this.root = el('div', { class: 'coins-section' });
    this.chartHost = el('div', { class: 'coins-chart' });
    this.sidebar = this.#sidebar();
    const width = settings.get('sx_coins_sidebar_width', 400);
    if (width) this.sidebar.style.width = `${width}px`;
    this.root.append(this.chartHost, this.#resizer(), this.sidebar);
    host.append(this.root);

    this.panesEl = el('div', { class: 'coins-panes' });
    this.chartHost.append(this.#layoutBar(), this.panesEl);
    this.#buildPanes();

    // Fila Фьючерсы/Спот schimbă piața aplicației: lista își reîncarcă datele pentru ea.
    this.unsubs.push(bus.on('market:aggregates', debounce(() => {
      if (this.dataMarket !== this.app.market) this.#loadData();
    }, 300)));
    this.#loadData();
    this.#loadSparks();
    this.dataTimer = setInterval(() => this.#loadData(), DATA_MS);
    this.sparkTimer = setInterval(() => this.#loadSparks(), SPARK_MS);
  }

  // ------------------------------------------------------------ date pe bursă

  #exList() {
    return this.exSel === 'both' ? ['binance', 'bybit'] : [this.exSel];
  }

  async #loadData() {
    const m = this.app.market;
    await Promise.all(this.#exList().map(async (ex) => {
      try {
        const d = await (await fetch(`/api/aggregates?exchange=${ex}&market=${m}`)).json();
        this.data.set(`${ex}:${m}`, d.data || {});
      } catch { /* bursa căzută: rămân datele de data trecută */ }
    }));
    this.dataMarket = m;
    this.#renderList();
  }

  /** Mini-graficele, doar pentru modul carduri (tabelul nu le arată). */
  async #loadSparks() {
    if (this.view !== 'cards') return;
    const m = this.app.market;
    await Promise.all(this.#exList().map(async (ex) => {
      try {
        const d = await (await fetch(`/api/sparklines?exchange=${ex}&market=${m}`)).json();
        this.sparks.set(`${ex}:${m}`, new Map(Object.entries(d.data || {})));
      } catch { /* opționale */ }
    }));
    this.#renderList();
  }

  /** Moneda aleasă: graficele trec pe bursa ei, apoi pe ea. */
  async #open(r) {
    const m = this.app.market;
    settings.set(EX_CHART_KEY, r.ex);
    for (const p of this.panels || []) {
      if (p.exchange !== r.ex || p.market !== m) await p.setExchange(r.ex, m);
    }
    await this.panel.setSymbol(r.symbol);
    this.#renderList();
  }

  // ------------------------------------------------------ aranjarea graficelor

  /**
   * Unul, două sau patru grafice pe aceeași monedă, fiecare cu intervalul lui.
   *
   * Din videoclipul lor: aceeași monedă pe minut, cinci minute și oră, ca să vezi
   * dintr-o privire pe ce interval se decide mișcarea. Moneda e comună tuturor
   * panourilor — altfel ar fi patru screenere, nu patru vederi ale aceluiași lucru.
   */
  #layoutBar() {
    const curent = settings.get('sx_coins_layout', 1);
    this.layoutBtns = [1, 2, 4].map((n) => el('button', {
      class: `coins-lay${n === curent ? ' active' : ''}`,
      title: `${n} график${n > 1 ? 'а' : ''}`,
      dataset: { n: String(n) },
      onclick: () => this.#setLayout(n),
    }, [icon(n === 1 ? 'square' : n === 2 ? 'columns' : 'grid', 12), el('span', { text: String(n) })]));
    return el('div', { class: 'coins-laybar' }, this.layoutBtns);
  }

  #setLayout(n) {
    this.#saveTfs();
    settings.set('sx_coins_layout', n);
    for (const b of this.layoutBtns) b.classList.toggle('active', Number(b.dataset.n) === n);
    this.#buildPanes();
  }

  #buildPanes() {
    for (const p of this.panels || []) p.destroy();
    clear(this.panesEl);

    const n = settings.get('sx_coins_layout', 1);
    this.panesEl.className = `coins-panes n${n}`;
    const tfs = settings.get('sx_coins_pane_tfs', ['1m', '5m', '15m', '1h']);
    const symbol = settings.get('sx_coins_symbol', 'BTCUSDT');

    this.panels = [];
    for (let i = 0; i < n; i++) {
      const host = el('div', { class: 'coins-pane' });
      this.panesEl.append(host);
      const panel = new ChartPanel({
        exchange: settings.get(EX_CHART_KEY, this.app.exchange),
        market: this.app.market,
        symbol,
        tf: tfs[i] || '5m',
        // Uneltele de desen doar pe primul: pe patru panouri ar mânca tot spațiul.
        showToolbar: n === 1,
        onSymbolChange: (s) => this.#propagate(s, panel),
      }).mount(host);

      // Intervalul fiecărui panou se reține imediat ce e schimbat, nu la ieșire:
      // altfel o reîncărcare a paginii l-ar pierde.
      const setTf = panel.setTimeframe.bind(panel);
      panel.setTimeframe = async (tf) => { await setTf(tf); this.#saveTfs(); };

      this.panels.push(panel);
    }
    // Restul codului (și `app.openSymbol`) vorbește cu un singur panou.
    this.panel = this.panels[0];
    this.#renderList();
  }

  #saveTfs() {
    if (!this.panels?.length) return;
    const tfs = [...settings.get('sx_coins_pane_tfs', ['1m', '5m', '15m', '1h'])];
    this.panels.forEach((p, i) => { tfs[i] = p.tf; });
    settings.set('sx_coins_pane_tfs', tfs);
  }

  /** Moneda aleasă într-un panou se duce în toate celelalte. */
  #propagate(symbol, sursa) {
    settings.set('sx_coins_symbol', symbol);
    for (const p of this.panels) {
      if (p !== sursa && p.symbol !== symbol) p.setSymbol(symbol);
    }
    this.#renderList();
  }

  /** Punctul de intrare pentru căutarea globală și pentru lista din dreapta. */
  setSymbol(symbol) {
    return this.panels?.[0]?.setSymbol(symbol);
  }

  unmount() {
    this.#closePanel();
    clearInterval(this.dataTimer);
    clearInterval(this.sparkTimer);
    this.offDrop?.();
    this.unsubs.forEach((fn) => fn());
    this.unsubs = [];
    this.#saveTfs();
    for (const p of this.panels || []) p.destroy();
    this.root?.remove();
  }

  // ------------------------------------------------------------------ sidebar

  #sidebar() {
    const collapsed = settings.get('sx_coins_sidebar_visible', true) === false;
    const bar = el('div', { class: `coins-sidebar${collapsed ? ' collapsed' : ''}` });

    this.tabsEl = el('div', { class: 'coins-tabs' }, [
      this.#tab('futures', 'Фьючерсы'),
      this.#tab('spot', 'Спот'),
      this.#tab('bookmarks', 'Закладки', 'bookmark'),
    ]);

    const searchInput = el('input', {
      class: 'search-input',
      placeholder: 'Поиск монеты...',
      oninput: debounce((e) => { this.search = e.target.value.trim().toUpperCase(); this.#renderList(); }, 180),
    });


    // Sugestii pe toate bursele, ca în terminal: alegi direct unde deschizi moneda.
    this.offDrop = attachSearchDropdown(searchInput, (item) => {
      searchInput.value = '';
      this.search = '';
      this.app.openInstrument(item);
    });

    // Un singur rând: căutare · preset · filtre · vedere · setări. Filtrele și sortarea stau
    // într-un panou; sub rând — doar filtrele active, ca etichete (și doar când există).
    this.viewBtn = el('button', {
      class: 'ct-icon', onclick: () => {
        this.view = this.view === 'table' ? 'cards' : 'table';
        settings.set('sx_coins_view', this.view);
        this.#syncViewBtn();
        this.#renderList();
        this.#loadSparks();
      },
    });
    this.#syncViewBtn();
    this.presetDD = this.#presetDD();
    this.fltCount = el('span', { class: 'ct-badge' });
    this.fltBtn = el('button', {
      class: 'ct-icon ct-flt', title: 'Фильтры и сортировка',
      onclick: (e) => { e.stopPropagation(); this.#togglePanel(); },
    }, [icon('filter', 11), this.fltCount]);
    this.chipsEl = el('div', { class: 'ct-chips' });
    this.panelEl = this.#panel();
    this.exDD = dropdown({
      className: 'fx-dd cf-dd ct-exdd', items: EXCHANGES, value: this.exSel, title: 'Биржа списка',
      onSelect: (v) => {
        this.exSel = v;
        settings.set(EX_KEY, v);
        this.#loadData();
        this.#loadSparks();
      },
    });
    const tools = el('div', { class: 'ct-tools' }, [
      el('div', { class: 'coins-search ct-search' }, [icon('search', 12), searchInput]),
      this.exDD,
      this.presetDD,
      this.fltBtn,
      this.viewBtn,
      el('button', { class: 'ct-icon', title: 'Настройки списка', onclick: () => this.#openListSettings() }, [icon('sliders', 11)]),
    ]);
    this.#syncFilters();

    this.listEl = el('div', { class: 'coins-list' });
    this.countEl = el('span', { class: 'coins-count' });

    bar.append(
      this.tabsEl,
      tools,
      this.chipsEl,
      this.headEl = el('div', { class: 'ct-head' }),
      this.listEl,
      el('div', { class: 'coins-foot' }, [
        this.countEl,
        el('button', {
          class: 'coins-collapse',
          onclick: () => {
            const visible = settings.get('sx_coins_sidebar_visible', true) === false;
            settings.set('sx_coins_sidebar_visible', visible);
            bar.classList.toggle('collapsed', !visible);
          },
        }, [icon('chevronRight', 13), el('span', { text: 'Скрыть список' })]),
      ]),
    );
    return bar;
  }

  #tab(id, label, iconName = null) {
    return el('button', {
      class: `coins-tab${id === this.tab ? ' active' : ''}`,
      onclick: async () => {
        this.tab = id;
        [...this.tabsEl.children].forEach((c) => c.classList.toggle('active', c.dataset.id === id));
        // Фьючерсы/Спот schimbă piața globală; Закладки filtrează piața curentă.
        if ((id === 'futures' || id === 'spot') && id !== this.app.market) {
          await this.app.setMarket(id);
          return;
        }
        this.#renderList();
      },
      dataset: { id },
    }, [iconName ? icon(iconName, 12) : null, el('span', { text: label })]);
  }

  #cycleSort(delta) {
    this.#setSort((this.sortIndex + delta + SORTS.length) % SORTS.length);
  }

  #setSort(index, desc = this.desc) {
    this.sortIndex = index;
    this.desc = desc;
    settings.set('sx_coins_sort', SORTS[index].id);
    this.#syncFilters();
    this.#renderList();
  }

  #syncViewBtn() {
    clear(this.viewBtn);
    this.viewBtn.append(icon(this.view === 'table' ? 'rows' : 'grid', 11));
    this.viewBtn.title = this.view === 'table' ? 'Сейчас: таблица. Переключить на карточки' : 'Сейчас: карточки. Переключить на таблицу';
  }

  // ----------------------------------------------------------------- filtre

  /** Presetul, compact: iconița și numele, fără prefix. */
  #presetDD() {
    return dropdown({
      className: 'fx-dd cf-dd ct-preset',
      items: [...FILTER_PRESETS.map((p) => ({ value: p.id, label: p.label, icon: p.icon })),
        { value: 'custom', label: 'Свой', icon: 'pencil' }],
      value: this.flt.preset || 'custom',
      title: 'Пресет фильтров',
      onSelect: (v) => {
        const p = FILTER_PRESETS.find((x) => x.id === v);
        if (p) {
          this.flt = { ...p.f, preset: v };
          const i = SORTS.findIndex((x) => x.id === p.sort);
          if (i >= 0) { this.sortIndex = i; this.desc = true; settings.set('sx_coins_sort', p.sort); }
        } else {
          this.flt.preset = 'custom';
        }
        this.#saveFilters();
      },
    });
  }

  #setFilter(key, v) {
    this.flt = { ...this.flt, [key]: v, preset: 'custom' };
    this.presetDD.setValue('custom');
    this.#saveFilters();
  }

  #saveFilters() {
    settings.set(FILTER_KEY, this.flt);
    this.#syncFilters();
    this.#renderList();
  }

  /** Panoul: fiecare filtru — un rând de variante, plus sortarea. Un clic, fără liste. */
  #panel() {
    this.pillRows = {};
    const panel = el('div', { class: 'fx-panel ct-panel', onmousedown: (e) => e.stopPropagation() });
    panel.append(el('div', { class: 'fx-panel-head' }, [
      el('span', { class: 'fx-panel-title' }, [icon('filter', 12), el('span', { text: 'Фильтры' })]),
      el('button', {
        class: 'fx-reset', onclick: () => { this.flt = { natr5: 0, range5: 0, vol: 0, volat: 0, preset: 'all' }; this.presetDD.setValue('all'); this.#saveFilters(); },
      }, [icon('undo', 10), el('span', { text: 'Сбросить' })]),
      el('button', { class: 'fx-x', title: 'Закрыть', onclick: () => this.#closePanel() }, [icon('close', 12)]),
    ]));
    const rand = (k, label, ic, hint, pills) => {
      this.pillRows[k] = pills;
      panel.append(el('div', { class: 'fx-frow' }, [
        el('div', { class: 'fx-frow-k' }, [icon(ic, 11), el('span', { text: label })]),
        el('div', { class: 'fx-pills' }, pills),
        hint ? el('div', { class: 'fx-frow-hint', text: hint }) : null,
      ].filter(Boolean)));
    };
    for (const fd of FILTERS) {
      rand(fd.key, fd.prefix, fd.lead, fd.title, fd.opts.map(([v, t]) => el('button', {
        class: 'fx-pill', text: t, dataset: { v: String(v) }, onclick: () => this.#setFilter(fd.key, Number(v) || 0),
      })));
    }
    rand('sort', 'Сортировка', 'fa-arrow-down-wide-short', null, [
      ...SORTS.map((x, i) => el('button', {
        class: 'fx-pill', text: x.label, dataset: { v: x.id },
        onclick: () => this.#setSort(i, this.sortIndex === i ? !this.desc : true),
      })),
    ]);
    return panel;
  }

  #togglePanel() {
    if (this.panelEl.isConnected) { this.#closePanel(); return; }
    document.body.append(this.panelEl);
    const r = this.fltBtn.getBoundingClientRect();
    const w = Math.min(420, window.innerWidth - 16);
    this.panelEl.style.width = `${w}px`;
    this.panelEl.style.left = `${Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8))}px`;
    this.panelEl.style.top = `${r.bottom + 6}px`;
    this.fltBtn.classList.add('open');
    this.offPanel = (e) => {
      if (e.type === 'keydown' && e.key !== 'Escape') return;
      if (e.type === 'mousedown' && this.fltBtn.contains(e.target)) return;
      this.#closePanel();
    };
    document.addEventListener('mousedown', this.offPanel);
    document.addEventListener('keydown', this.offPanel);
  }

  #closePanel() {
    if (!this.panelEl?.isConnected) return;
    this.panelEl.remove();
    this.fltBtn.classList.remove('open');
    document.removeEventListener('mousedown', this.offPanel);
    document.removeEventListener('keydown', this.offPanel);
  }

  /** Etichetele filtrelor active, numărul de pe buton și variantele aprinse din panou. */
  #syncFilters() {
    if (!this.chipsEl) return;
    clear(this.chipsEl);
    let n = 0;
    for (const fd of FILTERS) {
      const v = this.flt[fd.key] || 0;
      for (const p of this.pillRows?.[fd.key] || []) p.classList.toggle('active', Number(p.dataset.v) === v);
      if (!v) continue;
      n++;
      const t = fd.opts.find(([x]) => x === v)?.[1] ?? v;
      this.chipsEl.append(el('button', {
        class: 'ct-chip', title: 'Убрать фильтр', onclick: () => this.#setFilter(fd.key, 0),
      }, [el('span', { text: `${fd.prefix} ${t}` }), icon('close', 8)]));
    }
    const sortId = SORTS[this.sortIndex]?.id;
    for (const p of this.pillRows?.sort || []) p.classList.toggle('active', p.dataset.v === sortId);
    this.chipsEl.hidden = !n;
    this.fltCount.textContent = n ? String(n) : '';
    this.fltBtn.classList.toggle('on', n > 0);
  }

  #passes(r) {
    const f = this.flt;
    if (f.natr5 && natr5Of(r) < f.natr5) return false;
    if (f.range5 && !(Number(r.range5) >= f.range5)) return false;
    if (f.vol && (Number(r.quoteVolume) || 0) < f.vol) return false;
    if (f.volat && (Number(r.volatility24h) || 0) < f.volat) return false;
    return true;
  }

  /** Ce coloane suplimentare arată lista — echivalentul `coins-list-settings` de la ScalpX. */
  #openListSettings() {
    const columns = [
      ['spark', 'Мини-график', 'Динамика цены за 24 часа'],
      ['price', 'Цена', 'Текущая цена вместо изменения'],
      ['volatility', 'Волатильность', 'Размах между максимумом и минимумом суток'],
      ['natr', 'NATR', 'Нормализованный ATR'],
      ['correlation', 'Корреляция с BTC', 'От −1 до 1'],
    ];
    const current = { spark: true, ...(settings.get('sx_coins_list_settings', {}) || {}) };
    modal({
      title: 'Настройки списка',
      width: 440,
      body: el('div', { class: 'chart-settings' }, columns.map(([key, title, sub]) => toggle(
        current[key] !== false && current[key] !== undefined ? !!current[key] : false,
        (value) => {
          current[key] = value;
          settings.set('sx_coins_list_settings', current);
          this.#renderList();
        },
        { title, subtitle: sub },
      ))),
    });
  }

  /** Bara verticală dintre grafic și listă — trage pentru a schimba lățimea. */
  #resizer() {
    const bar = el('div', { class: 'coins-resizer', title: 'Потяните, чтобы изменить ширину' });
    let startX = 0;
    let startWidth = 0;

    const move = (event) => {
      const width = Math.max(260, Math.min(720, startWidth - (event.clientX - startX)));
      this.sidebar.style.width = `${width}px`;
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.body.style.userSelect = '';
      settings.set('sx_coins_sidebar_width', parseInt(this.sidebar.style.width, 10));
    };

    bar.addEventListener('mousedown', (event) => {
      startX = event.clientX;
      startWidth = this.sidebar.getBoundingClientRect().width;
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    });
    return bar;
  }

  // -------------------------------------------------------------------- listă

  #rows() {
    const bookmarks = new Set(settings.get('sx_bookmarks', []) || []);
    const m = this.app.market;
    let rows = [];
    for (const ex of this.#exList()) {
      const d = this.data.get(`${ex}:${m}`);
      if (d) {
        for (const [symbol, row] of Object.entries(d)) rows.push({ ...row, symbol, ex });
      } else if (ex === this.app.exchange) {
        // Până vin datele proprii: ce are deja aplicația pentru bursa ei.
        rows.push(...market.list().map((r) => ({ ...r, ex })));
      }
    }

    if (this.tab === 'bookmarks') rows = rows.filter((r) => bookmarks.has(r.symbol));
    if (this.search) rows = rows.filter((r) => r.symbol.includes(this.search));
    else rows = rows.filter((r) => this.#passes(r));   // căutarea găsește moneda oricum

    const field = SORTS[this.sortIndex].field;
    const val = (r) => (field === 'natr5' ? natr5Of(r) : Number(r[field]) || 0);
    rows.sort((a, b) => {
      const av = val(a);
      const bv = val(b);
      if (av === bv) return a.symbol.localeCompare(b.symbol) || a.ex.localeCompare(b.ex);
      return this.desc ? bv - av : av - bv;
    });
    return rows.slice(0, MAX_ROWS);
  }

  /** Antetul tabelului: coloanele se sortează la clic, ca în screenerul din video. */
  #renderHead(tabel) {
    clear(this.headEl);
    this.headEl.hidden = !tabel;
    if (!tabel) return;
    const cur = SORTS[this.sortIndex].id;
    const col = (id, text, title, cls = '') => {
      const i = SORTS.findIndex((x) => x.id === id);
      return el('button', {
        class: `ct-h ${cls}${cur === id ? ' on' : ''}`, title,
        onclick: () => (i >= 0 ? this.#setSort(i, cur === id ? !this.desc : true) : null),
      }, [el('span', { text }), cur === id ? icon(this.desc ? 'arrowDown' : 'arrowUp', 8) : null].filter(Boolean));
    };
    this.headEl.append(
      el('span', { class: 'ct-h ct-sym', text: 'Тикер' }),
      col('top_growth', 'Изм день', 'Изменение за 24 часа, %', 'num'),
      col('range5', 'Ренж 1м/5', 'Ход за последние 5 минут (макс − мин), %', 'num'),
      col('natr5', 'NATR 5м/14', 'Средний ход 5-минутной свечи, ATR 14, %', 'num'),
      col('volume', 'Объём 24ч', 'Оборот за 24 часа', 'num'),
    );
  }

  #tableRow(r, active, bookmarked) {
    const ch = Number(r.change24h) || 0;
    const n5 = natr5Of(r);
    const r5 = r.range5 === undefined ? null : Number(r.range5);
    const vol = Number(r.quoteVolume) || 0;
    // Intensitatea: valorile mari ies în evidență, ca în screenerul de referință.
    const nivel = (x, a, b) => (x >= b ? 'hi' : x >= a ? 'mid' : 'lo');
    return el('div', {
      class: `ct-row${active ? ' active' : ''}`,
      onclick: (e) => {
        if (e.ctrlKey || e.metaKey) { window.open(watchUrl({ symbol: r.symbol, exchange: r.ex, market: this.app.market }), '_blank'); return; }
        this.#open(r);
      },
      // Rotița mouse-ului: moneda într-un tab nou al browserului (multi window).
      onmousedown: (e) => { if (e.button === 1) e.preventDefault(); },
      onauxclick: (e) => {
        if (e.button !== 1) return;
        e.preventDefault();
        window.open(watchUrl({ symbol: r.symbol, exchange: r.ex, market: this.app.market }), '_blank');
      },
      title: `${r.symbol}${r.natr5 ? '' : ' · NATR оценён по 15м (точный считается для ликвидных фьючерсов)'}`,
    }, [
      el('span', { class: 'ct-sym' }, [
        el('button', {
          class: `ct-mark${bookmarked ? ' on' : ''}`, title: bookmarked ? 'Убрать из закладок' : 'В закладки',
          onclick: (e) => {
            e.stopPropagation();
            const marks = new Set(settings.get('sx_bookmarks', []) || []);
            if (marks.has(r.symbol)) marks.delete(r.symbol); else marks.add(r.symbol);
            settings.set('sx_bookmarks', [...marks]);
            this.#renderList();
          },
        }, [icon(bookmarked ? 'bookmarkOn' : 'bookmark', 10)]),
        iconMoneda(r.symbol, 15),
        el('b', { text: baseTicker(r.symbol) }),
        el('span', { class: 'ct-ex', title: r.ex === 'bybit' ? 'Bybit' : 'Binance' }, [iconBursa(r.ex, 11)]),
      ]),
      el('span', { class: `ct-c num ${ch >= 0 ? 'up' : 'down'} ${Math.abs(ch) >= 10 ? 'strong' : ''}`, text: fmtP(ch) }),
      el('span', { class: `ct-c num ${r5 === null ? 'lo' : nivel(r5, 1, 2)}`, text: r5 === null ? '—' : fmtP(r5) }),
      el('span', { class: `ct-c num ${nivel(n5, 1, 1.5)}${r.natr5 ? '' : ' est'}`, text: n5 ? fmtP(n5) : '—' }),
      el('span', { class: `ct-c num ${vol >= 100e6 ? 'hi' : 'lo'}`, text: fmtVolume(vol, '') }),
    ]);
  }

  #renderList() {
    if (!this.listEl) return;
    const rows = this.#rows();
    const bookmarks = new Set(settings.get('sx_bookmarks', []) || []);
    const maxVolume = rows.reduce((m, r) => Math.max(m, r.quoteVolume || 0), 1);
    const active = this.panel?.symbol;
    const activeEx = this.panel?.exchange;
    const m = this.app.market;

    // Păstrăm poziția de scroll — lista se redesenează la fiecare snapshot.
    const scroll = this.listEl.scrollTop;
    clear(this.listEl);

    if (!rows.length) {
      this.listEl.append(el('div', { class: 'coins-empty', text: this.tab === 'bookmarks' ? 'Закладок пока нет' : 'Ничего не найдено — ослабьте фильтры' }));
    }

    const tabel = this.view === 'table';
    this.listEl.classList.toggle('is-table', tabel);
    this.#renderHead(tabel);
    if (tabel) {
      for (const row of rows) this.listEl.append(this.#tableRow(row, row.symbol === active && row.ex === activeEx, bookmarks.has(row.symbol)));
      this.listEl.scrollTop = scroll;
      this.countEl.textContent = `${rows.length} монет`;
      return;
    }

    for (const row of rows) {
      this.listEl.append(coinRow(row, {
        spark: this.sparks.get(`${row.ex}:${m}`)?.get(row.symbol) ?? market.spark(row.symbol),
        active: row.symbol === active && row.ex === activeEx,
        maxVolume,
        exchange: row.ex,
        bookmarked: bookmarks.has(row.symbol),
        onClick: () => this.#open(row),
        onBookmark: (symbol) => {
          const marks = new Set(settings.get('sx_bookmarks', []) || []);
          if (marks.has(symbol)) marks.delete(symbol); else marks.add(symbol);
          settings.set('sx_bookmarks', [...marks]);
          this.#renderList();
        },
      }));
    }

    this.listEl.scrollTop = scroll;
    this.countEl.textContent = `${rows.length} монет`;
  }
}
